/**
 * Dashboard Page
 * Main overview page displaying key metrics, trends, and recent activity
 */

import React, { useState, useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Button,
  ButtonGroup,
  Fade
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getPrefs } from '../../utils/preferences';
import { isCommodityActive } from '../../utils/catalogStatus';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import {
  PageHeader,
  MetricCard,
  ChartCard,
  getChartStyles,
  chartColors,
  LoadingSkeleton,
  EmptyState
} from '../../components/common';
import {
  useDashboardMetrics,
  useFxSeries,
  usePriceMovements,
  useLocalPrices,
  useMarkets,
  useCommodities,
  useCategories,
} from '../../services/queries';
import { useThemeMode, MONO } from '../../context/ThemeContext';

const MarketIcon = () => <Icon icon="solar:shop-2-outline" width={20} height={20} />;
const CommodityIcon = () => <Icon icon="solar:box-outline" width={20} height={20} />;
const RecordIcon = () => <Icon icon="solar:document-text-outline" width={20} height={20} />;
const FXIcon = () => <Icon icon="solar:dollar-minimalistic-outline" width={20} height={20} />;

// Format ISO date (YYYY-MM-DD) to "18 Apr 2026"
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '-' : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Latest date across all local price records
const latestLocalDate = (rows) => {
  let max = null;
  for (const p of rows || []) { if (!max || p.date > max) max = p.date; }
  return max;
};

// Small muted "Updated · DATE" label style shared across cards.
// Color is intentionally omitted so it inherits from the parent Typography -
// that lets the accented (green) MetricCard render this in dark navy, while
// non-accent cards keep textMuted. Hardcoding a color here broke contrast on
// the green card in dark mode.
const updatedLabelSx = {
  fontSize: '0.68rem',
  fontStyle: 'italic',
  mt: 0.4,
  letterSpacing: '0.02em',
  opacity: 0.85,
};

// Small muted "Source: …" label style for chart card headers
const sourceLabelSx = {
  fontSize: '0.68rem',
  color: 'text.secondary',
  mt: 0.3,
};

// Monospace font style for numeric table values
const monoNumberStyle = { fontFamily: MONO };

// FX time-range options
const fxTimeRanges = [
  { label: '1W', weeks: 1 },
  { label: '1M', weeks: 4 },
  { label: '3M', weeks: 12 },
  { label: '6M', weeks: 24 },
];

// Process FX data for chart based on selected range. Cut by DATE, not point
// count: the CBN feed is daily, so "1M" must mean a month of days.
const getFxChartData = (rows, weeks) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - weeks * 7);
  const iso = cutoff.toISOString().slice(0, 10);
  return (rows || []).filter((r) => r.date >= iso).map((item) => ({
    date: item.date,
    rate: item.rate,
  }));
};

// Axis label for trend buckets: ISO days -> "23 Jul", months -> "Jan '26".
const fmtBucket = (d) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (/^\d{4}-\d{2}$/.test(d)) {
    const dt = new Date(`${d}-01`);
    return `${dt.toLocaleDateString('en-GB', { month: 'short' })} '${d.slice(2, 4)}`;
  }
  return d;
};

// Aggregate localPrices into a chart-friendly time series for the four
// commodities surfaced in this card. Standardises every record to ₦/100kg so
// commodities with different bag sizes (Ginger ships in 70kg bags) sit on a
// common axis.
const TREND_DEFS = [
  { id: 'COM005', key: 'maize', name: 'Maize', color: chartColors[0] },
  { id: 'COM001', key: 'rice', name: 'Rice', color: chartColors[1] },
  { id: 'COM018', key: 'soybean', name: 'Soybean', color: chartColors[3] },
  { id: 'COM028', key: 'ginger', name: 'Ginger', color: chartColors[4] },
];

const buildPriceTrends = (records, range, defs) => {
  const ids = defs.map((d) => d.id);
  const keyById = Object.fromEntries(defs.map((d) => [d.id, d.key]));
  const relevant = (records || []).filter((p) => ids.includes(p.commodityId));
  if (relevant.length === 0) return [];

  // The pills are TIME WINDOWS (like the fx chart beside this one), anchored
  // to the newest record: field data lands weekly, so anchoring to "today"
  // would blank the short ranges whenever uploads lag a few days.
  let latest = relevant[0].date;
  relevant.forEach((p) => { if (p.date > latest) latest = p.date; });
  const cut = new Date(`${latest.slice(0, 10)}T00:00:00Z`);
  if (range === 'W') cut.setUTCDate(cut.getUTCDate() - 7);
  else cut.setUTCMonth(cut.getUTCMonth() - ({ M: 1, '3M': 3, '6M': 6 }[range] || 6));
  const iso = cut.toISOString().slice(0, 10);

  // Short windows keep one point per record date (weekly field data); the
  // 6M window averages by calendar month so the line stays readable.
  const bucketKey = (date) => (range === '6M' ? date.slice(0, 7) : date.slice(0, 10));
  const buckets = {};
  relevant.filter((p) => p.date >= iso).forEach((p) => {
    const k = bucketKey(p.date);
    if (!buckets[k]) {
      buckets[k] = { date: k };
      ids.forEach((id) => { buckets[k][id] = { t: 0, c: 0 }; });
    }
    // `null / 100` is 0, so a row carrying neither figure used to be averaged
    // in as a real ₦0 per 100kg. Skip it instead: the bucket keeps its own
    // count, so one blank row no longer drags the line down.
    const perKg = p.pricePerKg != null
      ? p.pricePerKg
      : (p.price == null ? null : p.price / (p.bagSize || 100));
    if (perKg == null) return;
    buckets[k][p.commodityId].t += perKg * 100;
    buckets[k][p.commodityId].c += 1;
  });
  const rows = Object.values(buckets).map((w) => {
    const pt = { date: w.date };
    ids.forEach((id) => {
      if (w[id]?.c) pt[keyById[id]] = Math.round(w[id].t / w[id].c);
    });
    return pt;
  }).sort((a, b) => a.date.localeCompare(b.date));
  return rows;
};

// Per-state activity is derived from the live markets + price records inside
// the component (the old hardcoded table fabricated its figures).

function Dashboard() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  // Default chart windows come from the user's Settings preference. The fx
  // pills key by label ('1M') while the trends pills key by short code ('M').
  const prefRange = getPrefs(authUser?.email).chartRange;
  const [fxRange, setFxRange] = useState(['1M', '3M', '6M'].includes(prefRange) ? prefRange : '3M');
  const [trendRange, setTrendRange] = useState({ '1M': 'M', '3M': '3M', '6M': '6M' }[prefRange] || '3M');
  const { isDark } = useThemeMode();
  const chartStyles = getChartStyles(isDark);

  // Live data from the API (replaces the old mockData imports).
  const { data: metrics, isLoading: mLoading } = useDashboardMetrics();
  const { data: fx, isLoading: fxLoading } = useFxSeries('ALL');
  const { data: movements, isLoading: mvLoading } = usePriceMovements();
  const { data: local, isLoading: lpLoading } = useLocalPrices();
  const { data: commodities = [] } = useCommodities();
  const { data: commodityCategories = [] } = useCategories();

  // Live-tracking showcases (trend lines, top movers) exclude RETIRED
  // commodities (inactive commodity or category); historical displays keep
  // them. Missing catalog entries stay visible - never hide on unknowns.
  const trendDefs = useMemo(() => TREND_DEFS.filter((d) => {
    const c = commodities.find((x) => x.id === d.id);
    return !c || isCommodityActive(c, commodityCategories);
  }), [commodities, commodityCategories]);
  const loading = mLoading || fxLoading || mvLoading || lpLoading;

  const priceTrendData = useMemo(() => buildPriceTrends(local, trendRange, trendDefs), [local, trendRange, trendDefs]);

  // Real sparklines and trends derived from actual data - never invented
  // figures. Weekly record counts / distinct markets / distinct commodities
  // come from the local price records; the FX spark is the trailing closes.
  const weeklySparks = useMemo(() => {
    const byWeek = {};
    (local || []).forEach((p) => {
      if (!byWeek[p.date]) byWeek[p.date] = { records: 0, markets: new Set(), commodities: new Set() };
      byWeek[p.date].records += 1;
      byWeek[p.date].markets.add(p.marketId);
      byWeek[p.date].commodities.add(p.commodityId);
    });
    const weeks = Object.keys(byWeek).sort().slice(-8);
    return {
      records: weeks.map((w) => byWeek[w].records),
      markets: weeks.map((w) => byWeek[w].markets.size),
      commodities: weeks.map((w) => byWeek[w].commodities.size),
    };
  }, [local]);
  const fxSpark = useMemo(() => (fx || []).slice(-14).map((r) => r.rate), [fx]);

  // Real per-state activity: markets registered, records submitted, and
  // coverage = share of the state's markets that have reported at least once.
  const { data: marketList } = useMarkets();
  const marketActivityByState = useMemo(() => {
    if (!marketList?.length) return [];
    const stateOf = {};
    const agg = {};
    marketList.forEach((m) => {
      stateOf[m.id] = m.state;
      if (!agg[m.state]) agg[m.state] = { state: m.state, markets: 0, records: 0, reporting: new Set() };
      agg[m.state].markets += 1;
    });
    (local || []).forEach((p) => {
      const s = stateOf[p.marketId];
      if (!s) return;
      agg[s].records += 1;
      agg[s].reporting.add(p.marketId);
    });
    return Object.values(agg)
      .map((r) => ({ state: r.state, markets: r.markets, records: r.records, fillRate: r.markets ? Math.round((r.reporting.size / r.markets) * 100) : 0 }))
      .sort((a, b) => b.records - a.records);
  }, [marketList, local]);
  const fxLastClose = fx?.[fx.length - 1]?.rate ?? null;
  const fxPrevClose = fx?.[fx.length - 2]?.rate ?? null;
  const fxPct = fxLastClose != null && fxPrevClose ? ((fxLastClose - fxPrevClose) / fxPrevClose) * 100 : null;

  const alternatingRowSx = {
    '&:nth-of-type(even)': {
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa',
    },
    '&:hover': {
      bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc',
    },
  };

  const tableHeaderCellSx = {
    bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
    color: isDark ? '#cbd5e1' : undefined,
    fontWeight: 600,
  };

  const commodityByName = useMemo(() => new Map(commodities.map((c) => [c.name, c])), [commodities]);
  const topMovers = (movements || [])
    .filter((m) => {
      const c = commodityByName.get(m.topCommodity);
      return !c || isCommodityActive(c, commodityCategories);
    })
    .slice(0, 5)
    .sort((a, b) => Math.abs(b.priceChange) - Math.abs(a.priceChange));

  // Derive FX chart data from selected range
  const selectedRange = fxTimeRanges.find(r => r.label === fxRange) || fxTimeRanges[2];
  const fxChartData = getFxChartData(fx, selectedRange.weeks);

  if (loading) {
    return (
      <Box>
        <PageHeader
          eyebrow="Analytics"
          title="Dashboard"
          subtitle="Overview of commodity market intelligence across Nigeria"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Dashboard' }
          ]}
        />
        <LoadingSkeleton variant="page" />
      </Box>
    );
  }

  return (
    <Fade in={!loading} timeout={400}>
      <Box>
        <PageHeader
          eyebrow="Analytics"
          title="Dashboard"
          subtitle="Overview of commodity market intelligence across Nigeria"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Dashboard' }
          ]}
        />

        {/* Key Metrics Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Active Markets"
              value={metrics?.activeMarkets ?? 0}
              subtitle={
                <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                  <span>across 4 states</span>
                  <Box component="span" sx={updatedLabelSx}>Updated · {fmtDate(metrics?.lastUpdateDate)}</Box>
                </Box>
              }
              icon={<MarketIcon />}
              color="primary"
              accent
              sparklineData={weeklySparks.markets.length > 1 ? weeklySparks.markets : null}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Commodities Tracked"
              value={metrics?.trackedCommodities ?? 0}
              subtitle={
                <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                  <span>{`of ${metrics?.totalCommodities ?? 0} total variants`}</span>
                  <Box component="span" sx={updatedLabelSx}>Updated · {fmtDate(metrics?.lastUpdateDate)}</Box>
                </Box>
              }
              icon={<CommodityIcon />}
              color="secondary"
              sparklineData={weeklySparks.commodities.length > 1 ? weeklySparks.commodities : null}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Price Records"
              value={(metrics?.totalPriceRecords ?? 0).toLocaleString()}
              subtitle={
                <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                  <span>{`~${metrics?.avgFillRate ?? 0}% fill rate`}</span>
                  <Box component="span" sx={updatedLabelSx}>Updated · {fmtDate(latestLocalDate(local))}</Box>
                </Box>
              }
              icon={<RecordIcon />}
              color="info"
              sparklineData={weeklySparks.records.length > 1 ? weeklySparks.records : null}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="NGN/USD Rate"
              value={`₦${(metrics?.fxRate ?? 0).toLocaleString()}`}
              subtitle={
                <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                  <span>vs previous close</span>
                  <Box component="span" sx={updatedLabelSx}>Updated · {fmtDate(fx?.[fx.length - 1]?.date)}</Box>
                </Box>
              }
              icon={<FXIcon />}
              trend={fxPct == null ? undefined : fxPct >= 0 ? 'up' : 'down'}
              trendValue={fxPct == null ? undefined : `${fxPct >= 0 ? '+' : ''}${fxPct.toFixed(2)}%`}
              invertTrend
              color="warning"
              sparklineData={fxSpark.length > 1 ? fxSpark : null}
            />
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* FX Trend Chart */}
          <Grid item xs={12} md={6}>
            <ChartCard
              title="NGN/USD Exchange Rate Trend"
              subtitle={
                <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                  <span>{`Last ${selectedRange.label}`}</span>
                  <Box component="span" sx={sourceLabelSx}>Source: Central Bank of Nigeria (CBN)</Box>
                </Box>
              }
              height={280}
              actions={
                <ButtonGroup size="small" variant="outlined">
                  {fxTimeRanges.map((range) => (
                    <Button
                      key={range.label}
                      onClick={() => setFxRange(range.label)}
                      variant={fxRange === range.label ? 'contained' : 'outlined'}
                      sx={{
                        minWidth: 36,
                        fontSize: '0.7rem',
                        fontWeight: fxRange === range.label ? 700 : 500,
                      }}
                    >
                      {range.label}
                    </Button>
                  ))}
                </ButtonGroup>
              }
            >
              {fxChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={fxChartData} margin={{ right: 18 }}>
                  <defs>
                    <linearGradient id="fxGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...chartStyles.grid} />
                  <XAxis dataKey="date" {...chartStyles.axis} minTickGap={26} tickFormatter={fmtBucket} />
                  <YAxis
                    domain={['dataMin - 50', 'dataMax + 50']}
                    {...chartStyles.axis}
                    tickFormatter={(value) => `₦${value}`}
                  />
                  <Tooltip
                    labelFormatter={(d) => fmtDate(d)}
                    formatter={(value) => [value != null ? `₦${value.toLocaleString()}` : '-', 'Rate']}
                    contentStyle={chartStyles.tooltip.contentStyle}
                    labelStyle={chartStyles.tooltip.labelStyle}
                    itemStyle={chartStyles.tooltip.itemStyle}
                  />
                  <Area
                    type="monotone"
                    dataKey="rate"
                    stroke="#22C55E"
                    strokeWidth={2}
                    fill="url(#fxGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
              ) : (
                <EmptyState compact title="No FX data yet" description="Exchange-rate history will appear here once published." sx={{ height: '100%' }} />
              )}
            </ChartCard>
          </Grid>

          {/* Price Trends Chart */}
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Local Commodity Price Trends"
              subtitle={
                <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                  <span>Average price (₦/100kg)</span>
                  <Box component="span" sx={sourceLabelSx}>Source: Field Agents</Box>
                </Box>
              }
              height={280}
              legendItems={trendDefs.map((d) => ({ label: d.name, color: d.color }))}
              actions={
                <ButtonGroup size="small" variant="outlined">
                  {[
                    { v: 'W', label: '1W' },
                    { v: 'M', label: '1M' },
                    { v: '3M', label: '3M' },
                    { v: '6M', label: '6M' },
                  ].map((r) => (
                    <Button
                      key={r.v}
                      onClick={() => setTrendRange(r.v)}
                      variant={trendRange === r.v ? 'contained' : 'outlined'}
                    >
                      {r.label}
                    </Button>
                  ))}
                </ButtonGroup>
              }
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={priceTrendData} margin={{ right: 28 }}>
                  <CartesianGrid {...chartStyles.grid} />
                  <XAxis dataKey="date" {...chartStyles.axis} minTickGap={26} tickFormatter={fmtBucket} />
                  <YAxis {...chartStyles.axis} tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    labelFormatter={(d) => fmtBucket(d)}
                    formatter={(value, name) => [value != null ? `₦${value.toLocaleString()}` : '-', name]}
                    contentStyle={chartStyles.tooltip.contentStyle}
                    labelStyle={chartStyles.tooltip.labelStyle}
                    itemStyle={chartStyles.tooltip.itemStyle}
                  />
                  {trendDefs.map((d) => (
                    <Line key={d.key} type="monotone" dataKey={d.key} stroke={d.color} strokeWidth={2} name={d.name} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>

        {/* Lower Section */}
        <Grid container spacing={3}>
          {/* Market Activity by State */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="Market Activity by State"
                action={
                  <Button
                    size="small"
                    endIcon={<Icon icon="solar:arrow-right-outline" width={16} height={16} />}
                    onClick={() => navigate('/app/markets?tab=records')}
                  >
                    View All
                  </Button>
                }
              />
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderCellSx}>State</TableCell>
                        <TableCell align="center" sx={tableHeaderCellSx}>Markets</TableCell>
                        <TableCell align="center" sx={tableHeaderCellSx}>Records</TableCell>
                        <TableCell sx={tableHeaderCellSx}>Coverage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {marketActivityByState.map((row) => (
                        <TableRow
                          key={row.state}
                          hover
                          sx={alternatingRowSx}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Icon icon="solar:map-point-outline" width={16} height={16} color="#22C55E" />
                              {row.state}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={monoNumberStyle}>{row.markets}</TableCell>
                          <TableCell align="center" sx={monoNumberStyle}>{row.records.toLocaleString()}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={row.fillRate}
                                sx={{ flex: 1, height: 6, borderRadius: 3 }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={monoNumberStyle}>
                                {row.fillRate}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Price Movers */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="Top Price Movers"
                subheader="Markets with largest price changes"
                action={
                  <Button
                    size="small"
                    endIcon={<Icon icon="solar:arrow-right-outline" width={16} height={16} />}
                    onClick={() => navigate('/app/markets?tab=map')}
                  >
                    View Map
                  </Button>
                }
              />
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderCellSx}>Market</TableCell>
                        <TableCell sx={tableHeaderCellSx}>State</TableCell>
                        <TableCell sx={tableHeaderCellSx}>Top Commodity</TableCell>
                        <TableCell align="right" sx={tableHeaderCellSx}>
                          Change{' '}
                          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '0.72rem' }}>
                            (7-day)
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topMovers.map((market) => (
                        <TableRow
                          key={market.marketId}
                          hover
                          sx={alternatingRowSx}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {market.marketName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {market.state}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {market.topCommodity}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              icon={<Icon icon={market.priceChange > 0 ? 'solar:arrow-up-outline' : 'solar:arrow-down-outline'} width={14} height={14} style={{ marginLeft: 6 }} />}
                              label={`${market.priceChange > 0 ? '+' : ''}${market.priceChange}%`}
                              color={market.priceChange > 0 ? 'success' : 'error'}
                              variant="outlined"
                              sx={{ fontWeight: 600, ...monoNumberStyle }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Box>
    </Fade>
  );
}

export default Dashboard;
